import { wrap } from '@repo/di-ez';
import { CloseMiniLine, DoneLight } from '@repo/go-icons';
import { Button, Checkbox, Input, Modal, Radio } from '@repo/go-ui';
import { hasValue } from '@repo/utils';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStore } from 'src/store';
import { GOPLAN_TABLE_SYSTEM_TEMPLATES_EDITING } from 'src/store/roles-store/roles';

import type { DisplayManager } from '../table-settings/display-manager';

import type { WellsTableTemplateStore } from './wells-table-template.store';
import styles from './wells-table-templates.module.scss';

type Props = {
  displayManager: DisplayManager;
  templatesStore: WellsTableTemplateStore;
};

export const EditTemplateModal = wrap<Props>(function EditTemplateModal({ displayManager, templatesStore }) {
  const { t } = useTranslation();
  const { editTemplateState, setEditTemplateModalState, createOrUpdateTemplate } = templatesStore;
  const { rolesStore } = useStore();

  const editingTemplate = (() => {
    if (editTemplateState === 'new' || !hasValue(editTemplateState)) {
      return null;
    }

    return editTemplateState;
  })();

  const [modalTitle, setModalTitle] = useState<string>(t('wells:Templates.createTemplate'));
  const [doneText, setDoneText] = useState<string>(t('common:Buttons.create'));

  const defaultTemplateType = useMemo(() => {
    if (editingTemplate?.type) {
      return editingTemplate.type;
    }

    if (templatesStore.isPrivateCreationAvailable) {
      return 'private';
    }

    if (templatesStore.isSystemCreationAvailable) {
      return 'system';
    }

    return 'private';
  }, [editingTemplate?.type, templatesStore.isPrivateCreationAvailable, templatesStore.isSystemCreationAvailable]);

  const [templateType, setTemplateType] = useState<'private' | 'system'>(defaultTemplateType);
  const [templateName, setTemplateName] = useState<string>(editingTemplate?.name ?? '');
  const [templateDescription, setTemplateDescription] = useState<string>(editingTemplate?.description ?? '');
  const [isTemplateTypeVisible, setIsTemplateTypeVisible] = useState<boolean>(editTemplateState === 'new');
  const [applyCurrentTableSettingsToTemplate, setApplyCurrentTableSettingsToTemplate] = useState<boolean>(false);

  const clearForm = useCallback(() => {
    setTimeout(() => {
      setTemplateType('private');
      setTemplateName('');
      setTemplateDescription('');
      setIsTemplateTypeVisible(false);
      setApplyCurrentTableSettingsToTemplate(false);
    }, 300);
  }, []);

  const handleSubmit = useCallback(() => {
    const tableSettings = applyCurrentTableSettingsToTemplate ? displayManager.settings : null;

    createOrUpdateTemplate(
      templateName.trim(),
      templateDescription.trim(),
      templateType,
      editingTemplate,
      tableSettings,
      clearForm,
    );
  }, [
    applyCurrentTableSettingsToTemplate,
    displayManager.settings,
    createOrUpdateTemplate,
    templateName,
    templateDescription,
    templateType,
    editingTemplate,
    clearForm,
  ]);

  useLayoutEffect(() => {
    if (editTemplateState === 'new') {
      setModalTitle(t('wells:Templates.createTemplate'));
      setDoneText(t('common:Buttons.create'));
      setIsTemplateTypeVisible(true);
    } else if (hasValue(editTemplateState)) {
      setModalTitle(t('wells:Templates.editTemplate'));
      setDoneText(t('common:Buttons.save'));
    }
  }, [editTemplateState, modalTitle, t]);

  useEffect(() => {
    if (editingTemplate) {
      setTemplateName(editingTemplate.name);
      setTemplateDescription(editingTemplate.description ?? '');
      setTemplateType(editingTemplate.type);
    } else {
      setTemplateType(defaultTemplateType);
    }
  }, [defaultTemplateType, editingTemplate]);

  return (
    <Modal
      open={hasValue(editTemplateState)}
      title={<h2 className="h2">{modalTitle}</h2>}
      onClickOutside={null}
      onClose={() => {
        templatesStore.abortTemplatesUpdating();
        setEditTemplateModalState(null);
        clearForm();
      }}
    >
      <div className={styles.editTemplateModalWrapper}>
        {rolesStore.hasAccess([GOPLAN_TABLE_SYSTEM_TEMPLATES_EDITING]) && isTemplateTypeVisible && (
          <div className={styles.editTemplateModalRadioWrapper}>
            <Radio
              id="private"
              checked={templateType === 'private'}
              disabled={templatesStore.isTemplatesUpdating || !templatesStore.isPrivateCreationAvailable}
              onChange={() => setTemplateType('private')}
            >
              {t('wells:Templates.privateOne')}
            </Radio>
            <Radio
              id="system"
              checked={templateType === 'system'}
              disabled={templatesStore.isTemplatesUpdating || !templatesStore.isSystemCreationAvailable}
              onChange={() => setTemplateType('system')}
            >
              {t('wells:Templates.publicOne')}
            </Radio>
          </div>
        )}

        <div>
          <Checkbox
            checked={applyCurrentTableSettingsToTemplate}
            onChange={(v) => setApplyCurrentTableSettingsToTemplate(v)}
          >
            <p className={styles.editTemplateModalInputLabel}>
              {t('wells:Templates.applyCurrentTableSettingsToTemplate')}
            </p>
          </Checkbox>
        </div>

        <div className={styles.editTemplateModalInputWrapper}>
          <p className={styles.editTemplateModalInputLabel}>{t('wells:Templates.templateName')}</p>
          <Input
            value={templateName}
            placeholder={t('wells:Templates.enterTemplateName')}
            size="m"
            className={styles.editTemplateModalInput}
            disabled={templatesStore.isTemplatesUpdating}
            onChange={(e) => setTemplateName(e.target.value)}
          />
        </div>

        <div className={styles.editTemplateModalInputWrapper}>
          <p className={styles.editTemplateModalInputLabel}>{t('wells:Templates.templateDescription')}</p>
          <Input
            value={templateDescription}
            placeholder={t('wells:Templates.enterTemplateDescription')}
            size="m"
            className={styles.editTemplateModalInput}
            maxLength={80}
            disabled={templatesStore.isTemplatesUpdating}
            onChange={(e) => setTemplateDescription(e.target.value)}
          />
          <p className={styles.editTemplateModalInputLabel}>{t('wells:Templates.notMoreThan80Characters')}</p>
        </div>
        <div className={styles.editTemplateModalButtonsWrapper}>
          <Button
            variant="danger"
            className={styles.editTemplateModalButton}
            icon={<CloseMiniLine />}
            onClick={() => {
              templatesStore.abortTemplatesUpdating();
              setEditTemplateModalState(null);
              clearForm();
            }}
          >
            {t('common:Buttons.cancel')}
          </Button>
          <Button
            variant="success"
            loading={templatesStore.isTemplatesUpdating}
            className={styles.editTemplateModalButton}
            icon={<DoneLight />}
            disabled={templatesStore.isTemplatesUpdating || !templateName.trim()}
            onClick={handleSubmit}
          >
            {doneText}
          </Button>
        </div>
      </div>
    </Modal>
  );
});
